import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { Banner } from './states';

interface DetachModeDescription {
  mode: string;
  title: string;
  summary: string;
  interruptsFabricTraffic: boolean;
}

interface DetachAction {
  type: string;
  key: string;
  namespace: string;
}

interface RetainedObject extends DetachAction {
  reason: string;
  explanation: string;
}

interface DetachPlan {
  mode: string;
  remove: DetachAction[];
  retain: RetainedObject[];
  refusals: string[];
  warnings: string[];
  preservesLocalNetworking: boolean;
}

interface DetachRequest {
  id: string;
  status: string;
  computed: DetachPlan;
}

/**
 * Detaching one edge.
 *
 * The plan is shown before anything is committed, and both lists are shown —
 * what goes and what stays. Showing only the removals would leave an operator
 * guessing whether their own firewall rules were about to disappear, which is
 * the exact worry detachment has to answer.
 *
 * The local-access confirmation is a checkbox rather than a typed phrase because
 * it is a statement of fact the operator either can or cannot make, not a
 * difficulty gate. What makes it meaningful is that the backend refuses without
 * it.
 */
export function DetachPanel({ deviceId }: { deviceId: string }): JSX.Element {
  const t = useT();
  // Fetched rather than hardcoded: the description of what a mode does is
  // served by the same code that performs it, so the two cannot drift.
  const modes = useAsync(
    () => api.get<{ modes: DetachModeDescription[]; guarantees: string[] }>('/detachments/modes'),
    [],
  );
  const [mode, setMode] = useState('REMOVE_CONSOLE_CONFIG');
  const [localAccess, setLocalAccess] = useState(false);
  const [request, setRequest] = useState<DetachRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const call = async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const onPlan = async (): Promise<void> => {
    const res = await call(() =>
      api.post<DetachRequest>(`/detachments/devices/${deviceId}/plan`, {
        mode,
        localAccessConfirmed: localAccess,
      }),
    );
    if (res) setRequest(res);
  };

  const onConfirm = async (): Promise<void> => {
    if (!request) return;
    const res = await call(() => api.post<DetachRequest>(`/detachments/${request.id}/confirm`, {}));
    if (res) setRequest({ ...request, status: res.status });
  };

  const onCancel = async (): Promise<void> => {
    if (!request) return;
    await call(() => api.post(`/detachments/${request.id}/cancel`, {}));
    setRequest(null);
  };

  const selected = (modes.data?.modes ?? []).find((m) => m.mode === mode);
  const plan = request?.computed;
  const refused = (plan?.refusals.length ?? 0) > 0;

  return (
    <div className="card">
      <h2>{t('detach.title')}</h2>

      {error ? <Banner variant="error">{error}</Banner> : null}

      {request === null ? (
        <form
          className="form form--inline"
          onSubmit={(e) => {
            e.preventDefault();
            void onPlan();
          }}
        >
          <label className="field">
            <span>{t('detach.title')}</span>
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              {(modes.data?.modes ?? []).map((m) => (
                <option key={m.mode} value={m.mode}>
                  {m.title}
                </option>
              ))}
            </select>
          </label>

          {mode === 'REMOVE_CONSOLE_CONFIG' ? (
            <label className="field">
              <span>{t('detach.localAccess')}</span>
              <input
                type="checkbox"
                checked={localAccess}
                onChange={(e) => setLocalAccess(e.target.checked)}
              />
            </label>
          ) : null}

          <button type="submit" className="btn" disabled={busy}>
            {t('detach.plan')}
          </button>
        </form>
      ) : null}

      {request === null ? (
        <>
          {/* What this mode actually does, in the words of the code that does
              it — including whether it stops traffic. */}
          <p className="muted">{selected?.summary}</p>
          {mode === 'REMOVE_CONSOLE_CONFIG' ? (
            <p className="muted">{t('detach.localAccessWhy')}</p>
          ) : null}
          {modes.data ? (
            <>
              <h3>{t('detach.guarantees')}</h3>
              <ul className="muted">
                {modes.data.guarantees.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            </>
          ) : null}
        </>
      ) : null}

      {plan ? (
        <>
          {refused ? (
            <Banner variant="error">
              <strong>{t('detach.refused')}</strong>
              <ul>
                {plan.refusals.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </Banner>
          ) : null}

          {plan.warnings.map((w) => (
            <Banner variant="warning" key={w}>
              {w}
            </Banner>
          ))}

          <h3>{t('detach.willRemove')}</h3>
          {plan.remove.length === 0 ? (
            <p className="muted">{t('detach.nothingRemoved')}</p>
          ) : (
            <ul>
              {plan.remove.map((a) => (
                <li key={a.key}>
                  <code>{a.key}</code>
                </li>
              ))}
            </ul>
          )}

          {/* Shown alongside the removals so nobody has to guess whether their
              own configuration survives. */}
          <h3>{t('detach.willKeep')}</h3>
          <ul>
            {plan.retain.map((r) => (
              <li key={r.key}>
                <code>{r.key}</code> — {t(`detach.reason.${r.reason}`)}
              </li>
            ))}
          </ul>

          <div className="row-actions">
            {request.status === 'PLANNED' && !refused ? (
              <button
                type="button"
                className="btn btn--danger"
                disabled={busy}
                onClick={() => void onConfirm()}
              >
                {t('detach.confirm')}
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn--ghost"
              disabled={busy}
              onClick={() => void onCancel()}
            >
              {t('detach.cancel')}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
